import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { AbstractControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { InputControlBase } from '../../forms/input-control.base';
import { AstralFieldShellComponent } from '../astral-field-shell/astral-field-shell.component';
import { AstralFieldActionComponent } from '../astral-field-action/astral-field-action.component';
import { resolveAstralIconClass } from '../astral-icon-class.util';
import { AstralFieldAccessibilityDirective } from '../astral-field-accessibility.util';

@Component({
  selector: 'astral-input',
  standalone: true,
  imports: [NgTemplateOutlet, ReactiveFormsModule, TranslatePipe, AstralFieldShellComponent, AstralFieldActionComponent, AstralFieldAccessibilityDirective],
  templateUrl: './astral-input.component.html',
  styleUrls: ['./astral-input.component.scss'],
})
export class AstralInputComponent extends InputControlBase implements AfterViewInit, OnInit {
  private static nextId = 0;
  private readonly uid = `astral-input-${++AstralInputComponent.nextId}`;

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly router: Router
  ) {
    super();
  }

  @Input() accessibilityControl: AbstractControl | null;
  @Input() withShell = true;
  @Input() autocomplete = 'off';
  @Input() clearOthers = false;
  @Input() enableContactAction = true;
  @Input() filterActions = false;
  @Input() doNotDisplayClear = false;
  @Input() inputMode: 'text' | 'numeric' | 'decimal' | 'tel' | 'email' | 'url';
  @Input() inputId: string;
  @Input() inputName: string;
  @Input() integerOnly = false;
  @Input() placeholderText: string;
  @Input() span: number | 'full' = 1;
  @Input() supplementalLabel: string;
  @Input() textAlignCenter = false;
  @Input() translateValue = false;
  @Input() type: 'text' | 'number' | 'phone' | 'email' | 'date' | 'password' = 'text';
  @Input() focused = false;
  @Input() label: string;
  @Input() max: number;
  @Input() min: number;
  @Input() readonly = false;
  @Input() showRequiredMarker = true;
  @Input() step: number | 'any';
  // An omitted explicit state must defer to the reactive FormControl state.
  @Input() disabled: boolean | null = null;
  @Input() value: string | number;

  @Output() readonly addonClick = new EventEmitter();
  @Output() readonly clearClick = new EventEmitter();

  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  @HostBinding('tabindex') tabindex = -1;

  isFocused = false;
  passwordVisible = false;

  @HostBinding('style.grid-column')
  get hostGridColumn(): string | null {
    if (this.span === 'full') {
      return '1 / -1';
    }

    if (!this.span || this.span <= 1) {
      return null;
    }

    return `span ${this.span}`;
  }

  get resolvedInputId(): string {
    return this.inputId ?? this.uid;
  }

  get resolvedInputName(): string {
    if (this.inputName) {
      return this.inputName;
    }

    const source = this.control?.caption ?? this.label;
    const caption = source
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return caption || this.uid;
  }

  get resolvedDisabled(): boolean {
    return this.disabled ?? this.control?.disabled ?? false;
  }

  get resolvedLabel(): string | null {
    return this.label ?? null;
  }

  get resolvedReadonly(): boolean {
    return this.readonly || this.control?.isReadonly || false;
  }

  get readonlyIconClass(): string {
    return resolveAstralIconClass('lock') ?? '';
  }

  get resolvedInputType(): 'text' | 'number' | 'email' | 'date' | 'password' {
    if (this.integerOnly) {
      return 'text';
    }

    if (this.type === 'phone') {
      return 'text';
    }

    if (this.type === 'password' && this.passwordVisible && !this.isMaskedSecretPlaceholder) {
      return 'text';
    }

    return this.type;
  }

  get resolvedInputMode(): 'text' | 'numeric' | 'decimal' | 'tel' | 'email' | 'url' {
    return this.integerOnly ? 'numeric' : this.inputMode;
  }

  get isMaskedSecretPlaceholder(): boolean {
    return this.type === 'password' && /^[*•]{4,}$/.test(this.resolvedStringValue);
  }

  get resolvedSupplementalLabel(): string | null {
    return this.supplementalLabel ?? this.control?.supplementalLabel ?? null;
  }

  get phoneHref(): string | null {
    const value = this.resolvedStringValue;
    return value ? `tel:${value}` : null;
  }

  get resolvedStringValue(): string {
    const sourceValue = this.control ? this.control.value : this.value;
    if (sourceValue === undefined || sourceValue === null) {
      return '';
    }

    return String(sourceValue).trim();
  }

  get showCallAction(): boolean {
    return this.enableContactAction && !this.resolvedDisabled && this.type === 'phone' && !!this.phoneHref;
  }

  get showClearOthers(): boolean {
    const parentControls = this.control?.parent?.controls;

    if (this.resolvedDisabled || !this.clearOthers || !parentControls) {
      return false;
    }

    return Object.keys(parentControls).length > 1 && (this.filterActions || !!this.resolvedStringValue);
  }

  get showClearThis(): boolean {
    return (
      !!this.control &&
      !this.resolvedDisabled &&
      !this.resolvedReadonly &&
      !this.doNotDisplayClear &&
      (this.filterActions || !!this.resolvedStringValue)
    );
  }

  get showEmailAction(): boolean {
    return this.enableContactAction && !this.resolvedDisabled && this.type === 'email' && !!this.resolvedStringValue;
  }

  get showPasswordToggle(): boolean {
    return this.type === 'password' && !this.resolvedDisabled && !this.resolvedReadonly && !this.isMaskedSecretPlaceholder;
  }

  @HostListener('focus')
  focusHost() {
    this.input?.nativeElement.focus();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    if (this.type === 'email' && this.control) {
      this.control.addValidators(Validators.email);
      this.control.updateValueAndValidity({ emitEvent: false });
    }
  }

  ngAfterViewInit(): void {
    if (this.focused) {
      this.input?.nativeElement.focus();
      this.isFocused = true;
      this.cd.detectChanges();
    }
  }

  handleBlur() {
    this.isFocused = false;
    this.onblur();
  }

  handleFocus() {
    this.isFocused = true;
    this.onfocus();
  }

  handleIntegerBeforeInput(event: Event): void {
    if (!this.integerOnly) {
      return;
    }

    const inputEvent = event as InputEvent;
    if (inputEvent.data && !/^\d+$/.test(inputEvent.data)) {
      event.preventDefault();
    }
  }

  handleIntegerPaste(event: ClipboardEvent): void {
    if (!this.integerOnly) {
      return;
    }

    const value = event.clipboardData?.getData('text') ?? '';
    if (value && !/^\d+$/.test(value)) {
      event.preventDefault();
    }
  }

  handlePaste(event: ClipboardEvent): void {
    this.prepareMaskedSecretForEditing(event);
    this.handleIntegerPaste(event);
  }

  handleIntegerInput(event: Event): void {
    if (!this.integerOnly) {
      return;
    }

    const input = event.target as HTMLInputElement;
    if (/^\d*$/.test(input.value)) {
      return;
    }

    input.value = '';
    this.control?.setValue(undefined);
  }

  togglePasswordVisibility(): boolean {
    if (this.isMaskedSecretPlaceholder) {
      this.passwordVisible = false;
      return false;
    }

    this.passwordVisible = !this.passwordVisible;
    return false;
  }

  prepareMaskedSecretForEditing(event: KeyboardEvent | ClipboardEvent): void {
    if (!this.isMaskedSecretPlaceholder) {
      return;
    }

    if (event instanceof KeyboardEvent && !this.isEditingKey(event)) {
      return;
    }

    this.passwordVisible = false;
    this.input.nativeElement.value = '';
    this.control?.setValue('');
  }

  private isEditingKey(event: KeyboardEvent): boolean {
    return event.key.length === 1 || event.key === 'Backspace' || event.key === 'Delete';
  }

  clearAllOtherInputs(): boolean {
    const parentControls = this.control?.parent?.controls;
    if (!parentControls) {
      return false;
    }

    for (const controlName of Object.keys(parentControls)) {
      const currentControl = this.control.parent.get(controlName);
      if (currentControl !== this.control) {
        currentControl.setValue(undefined);
      }
    }

    this.control.parent.markAsDirty();
    this.addonClick.emit(this.control);
    return false;
  }

  clearInput(): boolean {
    if (!this.control) {
      return false;
    }

    this.onfocus();
    this.control.setValue(undefined);

    let form = this.control.parent;
    while (form?.parent) {
      form = form.parent;
    }

    form?.markAsDirty();
    this.onblur();
    this.clearClick.emit(this.control);
    return false;
  }

  sendEmail(): boolean {
    if (!this.resolvedStringValue) {
      return false;
    }

    this.router.navigateByUrl(`/dh/email/sendemail?tmpusr=${encodeURIComponent(this.resolvedStringValue)}`);
    return false;
  }
}
